import { Row, Col, Form, Input, Select, FormInstance } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CreatePlanAccountRequest,
  PlanAccountClassification,
  PlanAccountDestination,
  PlanAccountHierarchy,
  PlanAccountType,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { usePlanAccountLast } from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.hooks';
import { InputText } from '@/components/_common';
import { useState } from 'react';

interface AccountsPlanAccountsGeneralFormProps {
  form: FormInstance<CreatePlanAccountRequest & { subcode: string }>;
  editMode: boolean;
}

const defaultEmptyFieldError = {
  required: true,
  message: <FormattedMessage id="commons.form.errors.requiredField" />,
};

function AccountsPlanAccountsGeneralForm({
  form,
  editMode,
}: AccountsPlanAccountsGeneralFormProps) {
  const { Option } = Select;
  const intl = useIntl();

  const [sequential, setSequential] = useState('0000');
  const [hasDestination, setHasDestination] = useState<boolean>(false);

  const { mutateAsync: getLastPlanAccount } = usePlanAccountLast();

  const hierarchyOptions = [
    {
      value: PlanAccountHierarchy.Analitical,
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.hierarchy.analitical',
      }),
    },
    {
      value: PlanAccountHierarchy.Syntetics,
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.hierarchy.syntetics',
      }),
    },
  ];

  const typesOptions = [
    {
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.planType.commission',
      }),
      value: PlanAccountType.Commission,
    },
    {
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.planType.estorno',
      }),
      value: PlanAccountType.Estorno,
    },
    {
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.planType.general',
      }),
      value: PlanAccountType.General,
    },
    {
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.planType.tariff',
      }),
      value: PlanAccountType.Tariff,
    },
    {
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.planType.tax',
      }),
      value: PlanAccountType.Tax,
    },
    {
      label: intl.formatMessage({
        id: 'planAccounts.modal.general.form.planType.transfer',
      }),
      value: PlanAccountType.Transfer,
    },
  ];

  const watchHierarchy = Form.useWatch('hierarchy', form);

  const handleConvertSubcode = (value: string) => {
    if (value.length > 4) {
      let zeros = '';
      const splitValue = value.split('.')[1];
      const toNumber = (Number(splitValue) + 1).toString();
      for (let i = 0; i + toNumber.length < 4; i += 1) {
        zeros += '0';
      }
      return zeros + toNumber;
    } else {
      return '0001';
    }
  };

  const handleValuesChanged = (values: string) => {
    if (values.length === 4) {
      getLastPlanAccount({ code: values }).then((response) => {
        if (response.id && response.code.split('.')[0] === values) {
          form.setFieldsValue({
            subcode: handleConvertSubcode(response.code),
            destination: response.destination,
            hierarchy: PlanAccountHierarchy.Analitical,
          });
          setSequential(handleConvertSubcode(response.code));
          setHasDestination(true);
        } else {
          setSequential('0000');
          setHasDestination(false);
          form.setFieldsValue({
            subcode: '0000',
            hierarchy: PlanAccountHierarchy.Syntetics,
          });
        }
      });
    } else {
      setSequential('0000');
      setHasDestination(false);
      form.setFieldsValue({ subcode: '0000', hierarchy: undefined });
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      preserve={false}
      initialValues={{
        type: PlanAccountType.General,
      }}
    >
      <Row gutter={[16, 8]}>
        <Col span={8}>
          <FloatingFormItem
            name="code"
            style={{ width: '100%' }}
            label={intl.formatMessage({
              id: 'planAccounts.modal.general.form.code',
            })}
            rules={[defaultEmptyFieldError, { min: 4 }]}
          >
            <InputText
              disabled={editMode}
              onlyNumber
              maxLength={4}
              onChange={({ target: { value } }) => handleValuesChanged(value)}
              addonAfter={
                watchHierarchy === PlanAccountHierarchy.Analitical
                  ? `.${form.getFieldValue('subcode') || sequential}`
                  : ''
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={16}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'planAccounts.modal.general.form.description',
            })}
            name="description"
            rules={[defaultEmptyFieldError]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'planAccounts.modal.general.form.destination',
            })}
            name="destination"
            rules={[defaultEmptyFieldError]}
          >
            <Select disabled={hasDestination || editMode}>
              {Object.values(PlanAccountDestination).map((value) => (
                <Option key={value} value={value}>
                  <FormattedMessage
                    id={`planAccounts.modal.general.form.destination.${value.toLowerCase()}`}
                  />
                </Option>
              ))}
            </Select>
          </FloatingFormItem>
        </Col>

        <Col span={12} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'planAccounts.modal.general.form.hierarchy',
            })}
            name="hierarchy"
            rules={[defaultEmptyFieldError]}
          >
            <Select options={hierarchyOptions} disabled />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'planAccounts.modal.general.form.planType',
            })}
            name="type"
            rules={[defaultEmptyFieldError]}
          >
            <Select options={typesOptions} />
          </FloatingFormItem>
        </Col>

        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const destination = getFieldValue('destination');
            if (destination === PlanAccountDestination.Expense) {
              return (
                <>
                  <Col span={24}>
                    <FloatingFormItem
                      label={intl.formatMessage({
                        id: 'planAccounts.modal.general.form.classification',
                      })}
                      name="classification"
                      initialValue={
                        PlanAccountClassification.withoutClassification
                      }
                    >
                      <Select>
                        {Object.values(PlanAccountClassification).map(
                          (value) => (
                            <Option key={value} value={value}>
                              <FormattedMessage
                                id={`planAccounts.modal.general.form.classification.${value.toLowerCase()}`}
                              />
                            </Option>
                          ),
                        )}
                      </Select>
                    </FloatingFormItem>
                  </Col>
                </>
              );
            }
          }}
        </Form.Item>
      </Row>
      <Form.Item hidden noStyle name="subcode">
        <Input />
      </Form.Item>
    </Form>
  );
}

export default AccountsPlanAccountsGeneralForm;
